import React, { useEffect, useState } from 'react';
// import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/Dashboard.scss';
import '../../../../assets/css/_tooltip.scss';
import Service from '../services/Service';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import KeyMetrics from '../../Reports/components/KeyMetrics/KeyMetrics';
import Progress from '../../Reports/components/Progress/Progress';
// import Footer from '../../../../Components/Footer/Footer';
import MetricCard from '../../MetricCard/MetricCard';
import whoData from '../../Extras/swimscore_metrics.json';
import '../../Reports/components/DetailedAnalysis/DetailedAnalysis.scss';
import consultVideo from '../../../../assets/videos/Doctors office.mp4';
import mesh from '../../../../assets/videos/MeshGradient.mp4';
// import showToast from '../../../../Shared/Utils/ToastNotification';
import Swal from 'sweetalert2';

const HORMONE_RANGES = {
  'Total Testosterone': { normal: '300–916 ng/dL', low: '<300 ng/dL', high: '>916 ng/dL' },
  'Free Testosterone': { normal: '4.20–19.00 ng/dL', low: '<4.20 ng/dL', high: '>19.00 ng/dL' },
  SHBG: { normal: '16.5–56.0 nmol/L', low: '<16.5 nmol/L', high: '>56.0 nmol/L' },
  LH: { normal: '1.5–9.0 mIU/mL', low: '<1.5 mIU/mL', high: '>9.0 mIU/mL' },
  FSH: { normal: '1.5–12.0 mIU/mL', low: '<1.5 mIU/mL', high: '>12.0 mIU/mL' },
  Estradiol: { normal: '10–50 pg/mL', low: '<10 pg/mL', high: '>50 pg/mL' },
  Prolactin: { normal: '<25 ng/mL', low: '—', high: '>25 ng/mL (borderline); >30 ng/mL (high)' },
};

const getHormoneRange = (name) => {
  if (!name) return null;
  const key = Object.keys(HORMONE_RANGES).find((k) => name.toLowerCase().includes(k.toLowerCase()));
  return key ? HORMONE_RANGES[key] : null;
};

const Dashboard = ({ userId: propUserId, userName: propUserName, isAdminView = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [overall_score, setOverallScore] = useState(null);
  const [, setTestDate] = useState(null);
  const [currentTest, setCurrentTest] = useState(null);
  const [userData, setUserData] = useState({ testHistory: [] });
  const [, setDetailedMetrics] = useState([]);
  const [, setAvailableCategories] = useState([]);
  const [accordionStates, setAccordionStates] = useState({});
  const [currentOrder, setCurrentOrder] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null); // Track selected test from test history
  const [, setAllTestScoresForGraph] = useState([]); // Store all test scores for filtering
  const [reportType, setReportType] = useState('mailman'); // Track selected report type
  // const [hasOffering, setHasOffering] = useState(false); // Track if patient has offering

  // Separate state for mailman and tasso data
  const [mailmanMetrics, setMailmanMetrics] = useState([]);
  const [mailmanCategories, setMailmanCategories] = useState([]);
  const [tassoMetrics, setTassoMetrics] = useState([]);
  const [tassoCategories, setTassoCategories] = useState([]);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('mailman');
  const [availableAnalysisTabs, setAvailableAnalysisTabs] = useState(['mailman']); // Track which tabs to show

  const { user: loggedUser } = useSelector((state) => state.auth);

  const userId = propUserId || loggedUser?.id;
  const username = propUserName || loggedUser?.first_name || loggedUser?.username || 'User';

  const [activeSlide, setActiveSlide] = useState(0);
  const [activeMetricSlide, setActiveMetricSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [showSwimScoreModal, setShowSwimScoreModal] = useState(false);

  const tips = [
    {
      title: 'Optimize Sleep & Recovery',
      description: 'Prioritize 7-9 hours of quality sleep and manage stress to support sperm production and motility.',
    },
    {
      title: 'Nutrition & Hydration',
      description: 'Maintain a balanced diet rich in antioxidants and stay well-hydrated for optimal reproductive health.',
    },
    {
      title: 'Exercise Regularly',
      description: 'Engage in moderate physical activity to improve circulation and hormonal balance.',
    },
  ];

  const quickActions = [
    {
      title: 'Order new supplements',
      buttonText: 'Order new kit',
    },
    {
      title: 'Expert Consultation',
      buttonText: 'Plan a call',
    },
    {
      title: 'Export your data',
      buttonText: 'Download data',
    },
    {
      title: 'Track your progress',
      buttonText: 'View insights',
    },
  ];
  const totalActionSlides = Math.ceil(quickActions.length / 2);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (type) => {
    if (touchStart - touchEnd > 75) {
      if (type === 'tips') {
        setActiveSlide((prev) => (prev + 1) % tips.length);
      }
    }
    if (touchStart - touchEnd < -75) {
      if (type === 'tips') {
        setActiveSlide((prev) => (prev - 1 + tips.length) % tips.length);
      }
    }
  };

  // Helper function to find WHO cutoff based on metric name
  const findWhoCutoff = (metricName) => {
    if (!metricName) return null;
    const normalizedName = metricName.toLowerCase().trim();
    const matched = whoData.find((item) => item.metric.toLowerCase().includes(normalizedName) || normalizedName.includes(item.metric.toLowerCase()));
    return matched
      ? {
          who_cutoff: matched.who_cutoff,
          low: matched.low,
          normal: matched.normal,
          great: matched.great,
          range_unit: matched.unit,
          label_low: matched.label_low,
          label_normal: matched.label_normal,
          label_great: matched.label_great,
        }
      : null;
  };

  // Helper function to map ui_metrics from API to KeyMetrics format
  const mapUiMetricsToKeyMetrics = (uiMetrics) => {
    if (!uiMetrics || !Array.isArray(uiMetrics)) return [];

    return uiMetrics
      .filter((metric) => metric.value != null)
      .map((metric) => ({
        name: metric.ui_name || '',
        value: Number(metric.value) || 0,
        actualValue: Number(metric.value) || 0,
        unit: metric.unit || '',
        whoValue: metric.who_reference_point != null ? Number(metric.who_reference_point) : null,
        score: metric.score != null ? Number(metric.score) : null,
        description: metric.short_tooltip || '',
      }));
  };

  // Helper function to extract latest values from categories (current test data, not averages)
  const getLatestMetricsFromCategories = (categories, uiMetrics) => {
    if (!categories) return [];

    const safeNum = (v) => {
      const num = Number.isFinite(+v) ? +v : null;
      return num;
    };

    // Extract actual latest values from categories
    const motility = categories?.motility?.metrics || [];
    const morphology = categories?.morphology?.metrics || [];
    const concentration = categories?.concentration?.metrics || [];
    const dnaIntegrity = categories?.dnaIntegrity?.metrics || [];

    // Helper to find metric by name (case-insensitive, flexible matching)
    // Returns both value and unit from categories
    const findMetricValue = (metricsArray, possibleNames) => {
      for (const metric of metricsArray) {
        const metricName = (metric.name || '').toLowerCase().trim();
        for (const name of possibleNames) {
          if (metricName.includes(name.toLowerCase())) {
            return {
              value: safeNum(metric.value),
              unit: metric.unit || null,
            };
          }
        }
      }
      return null;
    };

    // Find actual latest values from categories with flexible name matching
    // Effective Swimmers (TMSC) maps to "Progressive Motile Sperm" from motility
    const progressiveMotileSperm = findMetricValue(motility, ['Progressive Motile Sperm', 'progressive motile sperm', 'Progressive motile']);

    // Forward Movement (PR%) maps to "Progressive motility"
    const progressiveMotility = findMetricValue(motility, ['Progressive motility', 'Progressive Motility', 'progressive motility', 'Progressive']);

    // Strong Forward Swimmers maps to "Rapid progressive"
    const rapidProgressive = findMetricValue(motility, ['Rapid progressive', 'Rapid Progressive', 'rapid progressive', 'Rapid progressive motility']);

    // Genetic Integrity (DFI) maps to "DNA fragmentation" from dnaIntegrity, fallback to immotile
    const dnaFragmentation = findMetricValue(dnaIntegrity, ['DNA fragmentation', 'dna fragmentation', 'DNA Fragmentation']);

    const immotile = findMetricValue(motility, ['Immotile', 'immotile']);

    // Shape & Structure maps to "Normal forms" from morphology
    const normalForms = findMetricValue(morphology, ['Normal forms', 'Normal Forms', 'normal forms']);

    // Sperm Count maps to "Concentration" from concentration
    const conc = findMetricValue(concentration, ['Concentration', 'concentration']);

    // Use ui_metrics as template for structure, but replace values with latest from categories
    if (!uiMetrics || !Array.isArray(uiMetrics)) return [];

    return uiMetrics
      .filter((metric) => metric.ui_name != null)
      .map((metric) => {
        const uiName = (metric.ui_name || '').toLowerCase();
        let categoryData = null;

        // Map ui_name to category metric and get DIRECT value from categories (not averages)
        // Effective Swimmers (TMSC) → Progressive Motile Sperm from motility
        if (uiName.includes('effective swimmers') || uiName.includes('tmsc')) {
          categoryData = progressiveMotileSperm;
        }
        // Strong Forward Swimmers → Rapid progressive from motility
        else if (uiName.includes('strong forward swimmers') || uiName.includes('rapid progressive') || uiName.includes('fast pr')) {
          categoryData = rapidProgressive;
        }
        // Genetic Integrity (DFI) → DNA fragmentation from dnaIntegrity, fallback to immotile
        else if (uiName.includes('genetic integrity') || uiName.includes('dfi') || uiName.includes('dna fragmentation')) {
          categoryData = dnaFragmentation || immotile;
        }
        // Forward Movement (PR%) → Progressive motility from motility
        else if (uiName.includes('forward movement') || (uiName.includes('progressive') && uiName.includes('pr%'))) {
          categoryData = progressiveMotility;
        }
        // Shape & Structure (Morphology) → Normal forms from morphology
        else if (uiName.includes('shape') || uiName.includes('structure') || (uiName.includes('morphology') && !uiName.includes('dna'))) {
          categoryData = normalForms;
        }
        // Sperm Count (Concentration) → Concentration from concentration
        else if (uiName.includes('sperm count') || uiName.includes('concentration')) {
          categoryData = conc;
        }

        // Use DIRECT value from categories only (no fallback to ui_metrics)
        // If category value exists, use it; otherwise use null/0
        const directValue = categoryData?.value != null ? categoryData.value : null;
        const directActualValue = directValue;

        return {
          name: metric.ui_name || '',
          value: directValue != null ? directValue : 0, // Use direct category value, no fallback
          actualValue: directActualValue != null ? directActualValue : 0, // Always use direct value from current test categories
          unit: categoryData?.unit || metric.unit || '', // Prefer unit from categories
          whoValue: metric.who_reference_point != null ? Number(metric.who_reference_point) : null,
          score: metric.score != null ? Number(metric.score) : null,
          description: metric.short_tooltip || '',
          officialName: metric.official_name || '',
        };
      });
  };

  // Reset selectedTest when navigating directly to dashboard (not from TestHistory)
  // Clear selectedTest if there's no orderId/orderNumber in location.state
  useEffect(() => {
    const selectedOrderId = location?.state?.orderId;
    const selectedOrderNumber = location?.state?.orderNumber;

    // If no orderId/orderNumber in location.state, clear selectedTest to show all scores
    // This ensures that direct navigation to dashboard shows all scores
    if (!selectedOrderId && !selectedOrderNumber) {
      setSelectedTest(null);
    }
  }, [location?.state]);

  // Listen for report type changes from navbar
  useEffect(() => {
    const handleReportTypeChange = (e) => {
      setReportType(e.detail.reportType);
    };
    window.addEventListener('reportTypeChanged', handleReportTypeChange);
    // Initialize from localStorage
    const savedType = localStorage.getItem('selectedReportType');
    if (savedType) setReportType(savedType);
    return () => window.removeEventListener('reportTypeChanged', handleReportTypeChange);
  }, []);

  // Check medication form status on component mount
  useEffect(() => {
    const checkMedicationFormStatus = async () => {
      if (isAdminView) return; // Skip for admin view

      try {
        const statusResponse = await Service.checkMedicationFormStatus();

        if (statusResponse.data && statusResponse.data.case_id_exists && !statusResponse.data.payment_status) {
          // Form completed but payment not done - show popup
          Swal.fire({
            title: 'Form Completed',
            text: 'Your intake form is already completed. Proceed to payment.',
            icon: 'success',
            confirmButtonText: 'Proceed to Payment',
            showCloseButton: true,
          }).then((result) => {
            if (result.isConfirmed) {
              navigate('/payment');
            }
          });
        }
      } catch (error) {
        // Silently handle error - don't show popup if API fails
        console.log('Medication form status check failed:', error);
      }
    };

    checkMedicationFormStatus();
  }, [isAdminView, navigate]);

  // Check if patient has offering
  // useEffect(() => {
  //   const checkOffering = async () => {
  //     try {
  //       const response = await Service.getPatientOffering();
  //       setHasOffering(!!response?.data?.offering_id);
  //     } catch (error) {
  //       setHasOffering(false);
  //     }
  //   };
  //   if (userId && !isAdminView) checkOffering();
  // }, [userId, isAdminView]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = sessionStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        const headers = { Authorization: `Bearer ${token}` };
        const response = await Service.getOrderList(userId, headers);
        let orders = response?.data || [];
        console.log('RAW API response:', JSON.stringify(response?.data, null, 2));

        // Remove duplicate orders based on order.id (Shopify order ID)
        const uniqueOrders = [];
        const seenOrderIds = new Set();
        orders.forEach((order) => {
          const orderId = order?.id;
          if (orderId && (typeof orderId === 'number' || typeof orderId === 'string') && !seenOrderIds.has(orderId)) {
            seenOrderIds.add(orderId);
            uniqueOrders.push(order);
          }
        });
        const allOrders = uniqueOrders;

        // Check if specific order is selected from test history
        const selectedOrderId = location?.state?.orderId;
        const selectedOrderNumber = location?.state?.orderNumber;

        let ordersToDisplay = allOrders;
        if (selectedOrderId || selectedOrderNumber) {
          // Filter to show ONLY the selected order
          ordersToDisplay = allOrders.filter((order) => {
            const orderId = order?.db_order?.id || order?.id;
            const orderNumber = order?.order_number;
            return (selectedOrderId && orderId === selectedOrderId) || (selectedOrderNumber && orderNumber === selectedOrderNumber);
          });
        }

        // Determine report type from the order(s) being displayed
        const reportTypesSet = new Set();
        ordersToDisplay.forEach((order) => {
          const types = order?.db_order?.type || [];
          if (Array.isArray(types)) {
            types.forEach((t) => {
              const normalized = t.toLowerCase();
              if (normalized === 'mailman' || normalized === 'tasso') {
                reportTypesSet.add(normalized);
              }
            });
          }
        });
        const availableTypes = Array.from(reportTypesSet);
        if (availableTypes.length === 0) availableTypes.push('mailman');

        let currentReportType = reportType;
        if (!availableTypes.includes(reportType)) {
          currentReportType = availableTypes[0];
          setReportType(currentReportType);
          localStorage.setItem('selectedReportType', currentReportType);
        }

        window.dispatchEvent(
          new CustomEvent('updateReportTypes', {
            detail: { reportType: currentReportType, availableTypes },
          })
        );

        // Filter by report type
        let filteredOrders = ordersToDisplay.filter((order) => {
          const types = order?.db_order?.type || [];
          if (!Array.isArray(types)) return currentReportType === 'mailman';
          return types.some((t) => t.toLowerCase() === currentReportType);
        });

        if (filteredOrders.length > 0) {
          // Collect test history from displayed orders only
          const allTestHistory = [];
          let latestOrderWithTest = null;
          let latestParsedData = null;
          let latestTestDate = null;

          // Build testHistory from filtered orders
          filteredOrders.forEach((order) => {
            const orderScore = order?.db_order?.swimscore_overall_score;
            const orderDate = order?.created_at || order?.db_order?.created_at;
            const orderId = order?.id || order?.db_order?.id;

            if (orderScore != null && typeof orderScore === 'number' && !isNaN(orderScore) && orderDate) {
              // Get test data (categories, etc.) from the latest test in this order for metrics display
              const pdfDocs = order?.db_order?.pdf_documents || [];
              let testDataForOrder = null;
              let uiMetricsForOrder = [];

              for (const pdfDoc of pdfDocs) {
                if (pdfDoc.mailman_order_id !== null) {
                  const parsedDataArray = pdfDoc?.parsed_data || [];
                  for (const parsedData of parsedDataArray) {
                    const orderTestHistory = parsedData?.data?.testHistory || [];
                    if (orderTestHistory && orderTestHistory.length > 0) {
                      // Get the latest test from this order for categories/metrics
                      testDataForOrder = orderTestHistory[orderTestHistory.length - 1];
                      uiMetricsForOrder = parsedData?.ui_metrics || [];

                      // Track the order with the latest test date
                      if (testDataForOrder?.date) {
                        const testDate = new Date(testDataForOrder.date);
                        if (!latestTestDate || testDate > latestTestDate) {
                          latestTestDate = testDate;
                          latestOrderWithTest = order;
                          latestParsedData = parsedData;
                        }
                      }
                      break; // Use first parsed_data with testHistory
                    }
                  }
                }
                if (testDataForOrder) break;
              }

              // Create test history entry using order-level swimscore_overall_score
              // Use order date for matching/sorting, but keep test date for display
              const testHistoryEntry = {
                date: orderDate, // Use order date for matching with graph data
                overallScore: Number(orderScore), // Use swimscore_overall_score from order
                orderId: orderId,
                orderNumber: order?.order_number,
                // Keep test data for metrics/categories display
                categories: testDataForOrder?.categories,
                ui_metrics: uiMetricsForOrder,
                testData: testDataForOrder, // Store original test data for reference (includes test.date)
              };

              allTestHistory.push(testHistoryEntry);
            }
          });

          // Sort by date (oldest first)
          allTestHistory.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateA - dateB;
          });

          // Collect test scores for overtime progress graph from displayed orders
          const testScoresForGraph = [];

          filteredOrders.forEach((order) => {
            const orderId = order?.id || order?.db_order?.id;
            const orderScore = order?.db_order?.mailman_reports?.[0]?.swimscore_overall_score;
            const orderDate = order?.created_at || order?.db_order?.created_at;

            // Use swimscore_overall_score from order level
            if (orderScore != null && typeof orderScore === 'number' && !isNaN(orderScore) && orderDate) {
              testScoresForGraph.push({
                score: Number(orderScore),
                date: orderDate,
                orderId: orderId,
                orderNumber: order?.order_number,
                orderData: order, // Store full order data for selection
              });
            }
          });

          // Remove duplicates: same date and score (in case same order appears multiple times)
          const allTestScores = [];
          const seenTests = new Set();
          testScoresForGraph.forEach((test) => {
            const testKey = `${test.date}_${test.score}_${test.orderId}`;
            if (!seenTests.has(testKey)) {
              seenTests.add(testKey);
              allTestScores.push(test);
            }
          });

          // Sort by date: newest to oldest (latest to last)
          allTestScores.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA; // Reverse: newest first
          });
          console.log(allTestScores, 'allTestScores');

          // Store all test scores in state
          setAllTestScoresForGraph(allTestScores);
          setOvertimeTestData(allTestScores);

          // Use selected test if available, otherwise latest
          let currentTestData = null;
          if (allTestHistory.length > 0) {
            currentTestData = allTestHistory[allTestHistory.length - 1];
          }

          // Calculate score from displayed orders
          const allOrderScores = [];
          let latestOrderDate = null;
          let orderForScore = null;

          filteredOrders.forEach((order) => {
            const orderScore = orderForScore?.db_order?.mailman_reports?.[0]?.parsed_data?.overall_score;
            if (orderScore != null && typeof orderScore === 'number' && !isNaN(orderScore)) {
              // Collect all valid scores for average calculation
              allOrderScores.push(orderScore);

              // Also track the most recent order for other purposes (test date, etc.)
              const orderCreatedAt = order?.created_at || order?.db_order?.created_at;
              if (orderCreatedAt && (!latestOrderDate || new Date(orderCreatedAt) > latestOrderDate)) {
                latestOrderDate = new Date(orderCreatedAt);
                orderForScore = order;
              }
            }
          });

          // Fallback to latestOrderWithTest or first order if no order found
          if (!orderForScore) {
            orderForScore = latestOrderWithTest || filteredOrders[0];
          }

          // Use latest order's score
          let score = null;
          let scoreTestDate = null;

          score = orderForScore?.db_order?.mailman_reports?.[0]?.parsed_data?.overall_score || null;
          // Get test date from latest order
          if (orderForScore) {
            const pdfDocs = orderForScore?.db_order?.pdf_documents || [];
            let latestTestDateObj = null;
            pdfDocs.forEach((pdfDoc) => {
              const parsedDataArray = pdfDoc?.parsed_data || [];
              parsedDataArray.forEach((parsedData) => {
                const orderTestHistory = parsedData?.data?.testHistory || [];
                if (orderTestHistory && orderTestHistory.length > 0) {
                  orderTestHistory.forEach((test) => {
                    if (test?.date) {
                      const testDateObj = new Date(test.date);
                      if (!latestTestDateObj || testDateObj > latestTestDateObj) {
                        latestTestDateObj = testDateObj;
                        scoreTestDate = test.date;
                      }
                    }
                  });
                }
              });
            });
          }

          // Get the latest test date from displayed orders
          let latestTestDateFromAllOrders = null;
          let latestTestDateObj = null;

          filteredOrders.forEach((order) => {
            const pdfDocs = order?.db_order?.pdf_documents || [];
            pdfDocs.forEach((pdfDoc) => {
              const parsedDataArray = pdfDoc?.parsed_data || [];
              parsedDataArray.forEach((parsedData) => {
                const orderTestHistory = parsedData?.data?.testHistory || [];
                if (orderTestHistory && orderTestHistory.length > 0) {
                  orderTestHistory.forEach((test) => {
                    if (test?.date) {
                      const testDateObj = new Date(test.date);
                      if (!latestTestDateObj || testDateObj > latestTestDateObj) {
                        latestTestDateObj = testDateObj;
                        latestTestDateFromAllOrders = test.date;
                      }
                    }
                  });
                }
              });
            });
          });

          // Store current order for PDF download
          setCurrentOrder(orderForScore);

          // Determine available tabs based on actual report data
          const availableTabs = [];
          const hasMailmanData = orderForScore?.db_order?.mailman_reports?.length > 0;
          const hasTassoData = orderForScore?.db_order?.tasso_reports?.length > 0;

          if (hasMailmanData) availableTabs.push('mailman');
          if (hasTassoData) availableTabs.push('tasso');
          if (availableTabs.length === 0) availableTabs.push('mailman');

          setAvailableAnalysisTabs(availableTabs);
          if (!availableTabs.includes(activeAnalysisTab)) {
            setActiveAnalysisTab(availableTabs[0]);
          }

          // Get ui_metrics from latest parsed_data, tasso_reports, or from db_order level
          const uiMetrics =
            latestParsedData?.ui_metrics ||
            orderForScore?.db_order?.mailman_reports?.[0]?.parsedata?.ui_metrics ||
            // orderForScore?.db_order?.tasso_reports?.[0]?.parsed_data?.ui_metrics ||
            // orderForScore?.db_order?.ui_metrics ||
            [];

          // Extract detailed metrics from categories for Detailed Analysis section
          let detailedMetricsList = [];
          let categoriesList = [];

          // Separate lists for mailman and tasso
          let mailmanMetricsList = [];
          let mailmanCategoriesList = [];
          let tassoMetricsList = [];
          let tassoCategoriesList = [];

          // Check if tasso_reports exists and has valid ui_metrics data
          const tassoReports = orderForScore?.db_order?.tasso_reports;
          const tassoUiMetrics = tassoReports?.[0]?.parsed_data?.ui_metrics;
          console.log(tassoUiMetrics, 'tassoUiMetrics');

          const tassoCategories_raw = tassoReports?.[0]?.parsed_data?.categories;
          const tassoDirectMetrics = tassoReports?.[0]?.parsed_data?.metrics;
          const hasValidTassoData =
            tassoReports &&
            Array.isArray(tassoReports) &&
            tassoReports.length > 0 &&
            ((Array.isArray(tassoUiMetrics) && tassoUiMetrics.length > 0) ||
              (tassoCategories_raw && typeof tassoCategories_raw === 'object') ||
              (Array.isArray(tassoDirectMetrics) && tassoDirectMetrics.length > 0));

          // Only process tasso data if it's valid and available
          console.log('hasValidTassoData', hasValidTassoData);
          console.log('tasso raw report:', tassoReports?.[0]);
          console.log('tassoUiMetrics:', tassoUiMetrics);
          console.log('tassoDirectMetrics:', tassoDirectMetrics);
          console.log('tassoCategories_raw:', tassoCategories_raw);
          if (hasValidTassoData) {
            // Build a name->value map from categories for filling null values in ui_metrics
            const categoryValueMap = {};
            if (tassoCategories_raw) {
              Object.values(tassoCategories_raw).forEach((catVal) => {
                (catVal.metrics || []).forEach((m) => {
                  if (m.name && m.value !== null && m.value !== undefined) {
                    categoryValueMap[m.name.toLowerCase()] = m.value;
                  }
                });
              });
            }

            // Build a secondary fallback map from pdf_documents where tasso_order_id is not null
            const pdfCategoryValueMap = {};
            const tassoPdfDoc = orderForScore?.db_order?.pdf_documents?.find((doc) => doc.tasso_order_id !== null);
            if (tassoPdfDoc) {
              const tassoPdfCategories = tassoPdfDoc?.parsed_data?.[0]?.data?.testHistory?.[0]?.categories;
              if (tassoPdfCategories) {
                Object.values(tassoPdfCategories).forEach((catVal) => {
                  (catVal.metrics || []).forEach((m) => {
                    if (m.name && m.value !== null && m.value !== undefined) {
                      pdfCategoryValueMap[m.name.toLowerCase()] = m.value;
                    }
                  });
                });
              }
            }
            console.log('categoryValueMap:', categoryValueMap);
            console.log('pdfCategoryValueMap:', pdfCategoryValueMap);
            // Support ui_metrics, categories object, or direct metrics array
            let tassoMetricsData = [];
            if (Array.isArray(tassoUiMetrics) && tassoUiMetrics.length > 0) {
              tassoMetricsData = tassoUiMetrics.map((m) => {
                console.log('raw tasso ui_metric entry:', m);
                const name = m.ui_name || m.name;
                const resolvedValue =
                  m.value !== null && m.value !== undefined
                    ? m.value
                    : (categoryValueMap[name?.toLowerCase()] ?? pdfCategoryValueMap[name?.toLowerCase()] ?? null);
                return { ...m, ui_name: name, value: resolvedValue };
              });
            } else if (Array.isArray(tassoDirectMetrics) && tassoDirectMetrics.length > 0) {
              tassoMetricsData = tassoDirectMetrics.map((m) => ({ ...m, ui_name: m.name }));
            } else if (tassoCategories_raw) {
              Object.entries(tassoCategories_raw).forEach(([catKey, catVal]) => {
                const catName = catVal.name || catKey;
                (catVal.metrics || []).forEach((m) => {
                  tassoMetricsData.push({ ...m, ui_name: m.name, category: catName });
                });
              });
            }
            console.log('tassoMetricsData (after mapping):', tassoMetricsData);
            // Group tasso metrics by category
            const tassoCategoriesMap = {};
            tassoMetricsData.forEach((metric) => {
              const category = metric.category || 'Hormones';
              if (!tassoCategoriesMap[category]) {
                tassoCategoriesMap[category] = {
                  name: category,
                  metrics: [],
                };
              }
              const hormoneRange = getHormoneRange(metric.ui_name);
              console.log(`hormoneRange for "${metric.ui_name}":`, hormoneRange);
              tassoCategoriesMap[category].metrics.push({
                name: metric.ui_name,
                value: metric.value,
                unit: metric.unit,
                status: metric.status === 'danger' ? 'error' : metric.status,
                score: metric.score,
                reference_range: metric.reference_range || metric.referenceRange,
                short_tooltip: metric.short_tooltip,
                who_cutoff: hormoneRange ? `Normal: ${hormoneRange.normal}` : metric.reference_range || metric.referenceRange || null,
                hormone_range: hormoneRange || null,
              });
            });

            // Add tasso categories to the tasso list
            Object.values(tassoCategoriesMap).forEach((category) => {
              tassoCategoriesList.push({
                name: category.name,
                score: 0, // Tasso reports don't have category scores
              });
              category.metrics.forEach((metric) => {
                tassoMetricsList.push({
                  ...metric,
                  categoryName: category.name,
                });
              });
            });
          }

          // Always try to get metrics from mailman/test data categories (fallback/primary for mailman reports)
          if (currentTestData && currentTestData.categories) {
            const categories = currentTestData.categories;
            // Flatten all metrics from all categories and collect category info
            Object.values(categories).forEach((category) => {
              if (category && category.name && category.metrics && Array.isArray(category.metrics) && category.metrics.length > 0) {
                // Add category to mailman categories list
                mailmanCategoriesList.push({
                  name: category.name,
                  score: category.score,
                });
                // Add metrics with category name
                category.metrics.forEach((metric) => {
                  const isDnaCategory = category.name.toLowerCase().includes('dna') || category.name.toLowerCase().includes('integrity');
                  const isDnaFragmentation =
                    (metric.name || '').toLowerCase().includes('dna fragmentation') || (metric.name || '').toLowerCase().includes('dfi');
                  if (isDnaCategory && !isDnaFragmentation) return;
                  mailmanMetricsList.push({
                    ...metric,
                    categoryName: category.name,
                  });
                });
              }
            });
          }
          console.log(tassoMetricsList, 'tassoMetricsList');

          // Set separate state for mailman and tasso
          setMailmanMetrics(mailmanMetricsList);
          setMailmanCategories(mailmanCategoriesList);
          setTassoMetrics(tassoMetricsList);
          setTassoCategories(tassoCategoriesList);

          // Keep legacy state for backward compatibility (combine both)
          detailedMetricsList = [...mailmanMetricsList, ...tassoMetricsList];
          categoriesList = [...mailmanCategoriesList, ...tassoCategoriesList];
          setDetailedMetrics(detailedMetricsList);
          setAvailableCategories(categoriesList);

          // Auto-select tab based on available data
          if (mailmanCategoriesList.length > 0) {
            setActiveAnalysisTab('mailman');
          } else if (tassoCategoriesList.length > 0) {
            setActiveAnalysisTab('tasso');
          }

          // Initialize accordion states - first category open by default on initial load
          if (categoriesList.length > 0) {
            setAccordionStates((prevStates) => {
              const currentCategoryNames = categoriesList.map((cat) => cat.name);
              const prevCategoryNames = Object.keys(prevStates);

              // Check if this is a completely new set of categories (e.g., different order selected)
              const isNewCategorySet =
                prevCategoryNames.length === 0 ||
                !currentCategoryNames.every((name) => prevCategoryNames.includes(name)) ||
                !prevCategoryNames.every((name) => currentCategoryNames.includes(name));

              const updatedStates = {};
              categoriesList.forEach((cat, index) => {
                // If it's a new category set, open the first one. Otherwise, preserve state if category exists
                if (isNewCategorySet) {
                  updatedStates[cat.name] = index === 0;
                } else if (cat.name in prevStates) {
                  updatedStates[cat.name] = prevStates[cat.name];
                } else {
                  updatedStates[cat.name] = false;
                }
              });

              return updatedStates;
            });
          }

          if (score !== null && score !== undefined) {
            setOverallScore(score);
          }

          // Use test date based on selected test or latest order
          if (scoreTestDate) {
            setTestDate(scoreTestDate);
          } else if (latestTestDateFromAllOrders) {
            setTestDate(latestTestDateFromAllOrders);
          } else if (orderForScore?.created_at || orderForScore?.db_order?.created_at) {
            setTestDate(orderForScore?.created_at || orderForScore?.db_order?.created_at);
          } else if (currentTestData?.date) {
            setTestDate(currentTestData.date);
          }

          // Update currentTest based on selected test or latest
          setCurrentTest(currentTestData);

          // Build currentTest and userData for KeyMetrics and Progress components
          // Use latest values from categories instead of potentially averaged ui_metrics
          if (currentTestData) {
            // Get previous test data if available
            const previousTestData = allTestHistory.length > 1 ? allTestHistory[allTestHistory.length - 2] : null;
            let previousTestObj = null;

            // Find ui_metrics for previous test by finding the parsed_data that contains it
            if (previousTestData) {
              let previousUiMetrics = [];
              let previousCategories = null;
              // Find previous test data - previousTestData already has the test data from allTestHistory
              if (previousTestData?.testData) {
                previousUiMetrics = previousTestData.ui_metrics || [];
                previousCategories = previousTestData.testData?.categories || null;
              } else {
                // Fallback: search in filtered orders
                filteredOrders.forEach((order) => {
                  const pdfDocs = order?.db_order?.pdf_documents || [];
                  pdfDocs.forEach((pdfDoc) => {
                    const parsedDataArray = pdfDoc?.parsed_data || [];
                    parsedDataArray.forEach((parsedData) => {
                      const orderTestHistory = parsedData?.data?.testHistory || [];
                      // Check if this parsed_data contains the previous test
                      const hasPreviousTest = orderTestHistory.some((test) => test.date === previousTestData.date);
                      if (hasPreviousTest && parsedData?.ui_metrics) {
                        previousUiMetrics = parsedData.ui_metrics;
                        // Get categories from the previous test
                        const prevTest = orderTestHistory.find((test) => test.date === previousTestData.date);
                        if (prevTest?.categories) {
                          previousCategories = prevTest.categories;
                        }
                      }
                    });
                  });
                });
              }

              if (previousUiMetrics.length > 0) {
                // Use latest values from previous test categories
                const previousKeyMetrics = previousCategories
                  ? getLatestMetricsFromCategories(previousCategories, previousUiMetrics)
                  : mapUiMetricsToKeyMetrics(previousUiMetrics);

                previousTestObj = {
                  date: previousTestData.date,
                  keyMetrics: previousKeyMetrics,
                };
              }
            }

            // Use latest values from current test categories instead of averaged ui_metrics
            const currentKeyMetrics =
              currentTestData.categories && uiMetrics.length > 0
                ? getLatestMetricsFromCategories(currentTestData.categories, uiMetrics)
                : uiMetrics.length > 0
                  ? mapUiMetricsToKeyMetrics(uiMetrics)
                  : [];

            const currentTestObj = {
              date: currentTestData.date,
              overall_score: score,
              rawSwimScore: score,
              trend: null,
              keyMetrics: currentKeyMetrics,
              previousTest: previousTestObj,
            };
            console.log(currentTestObj, 'currentTestObj');

            setCurrentTest(currentTestObj);

            // For Progress component, use all test history with ui_metrics attached
            const userDataObj = {
              name: username,
              testHistory: allTestHistory,
            };
            setUserData(userDataObj);
          } else {
            // No current test data available
            setCurrentTest(null);
            setUserData({ testHistory: [] });
            setDetailedMetrics([]);
            setAvailableCategories([]);
            setAccordionStates({});
            setCurrentOrder(null);
            setOvertimeTestData([]);
          }
        } else {
          setOverallScore(null);
          setTestDate(null);
          setCurrentTest(null);
          setUserData({ testHistory: [] });
          setDetailedMetrics([]);
          setAvailableCategories([]);
          setAccordionStates({});
          setCurrentOrder(null);
          setOvertimeTestData([]);
        }
      } catch (error) {
        console.error('❌ Failed to fetch orders:', error);
      }
    };
    console.log(currentTest, 'currentTest');

    if (userId) fetchOrders();
  }, [userId, username, location?.state?.orderId, location?.state?.orderNumber, reportType]);

  const [followupActive, setFollowupActive] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [overtimeTestData, setOvertimeTestData] = useState([]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 767);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle PDF download with report type selection
  const handleDownloadData = (reportType = null) => {
    if (!currentOrder) {
      alert('No order data available for download.');
      return;
    }

    const pdfDocuments = currentOrder?.db_order?.pdf_documents || [];
    if (pdfDocuments.length === 0) {
      alert('No PDF report available for this order.');
      return;
    }

    let selectedPdf;
    if (reportType === 'mailman') {
      selectedPdf = pdfDocuments.find((doc) => doc.mailman_order_id);
    } else if (reportType === 'tasso') {
      selectedPdf = pdfDocuments.find((doc) => doc.tasso_order_id);
    } else {
      selectedPdf = pdfDocuments[0];
    }

    if (!selectedPdf?.presigned_url) {
      alert(`No ${reportType} PDF report available for this order.`);
      return;
    }

    const link = document.createElement('a');
    link.href = selectedPdf.presigned_url;
    link.download = selectedPdf.title || `order_${currentOrder.order_number}_report.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get available report types
  const getAvailableReports = () => {
    const pdfDocuments = currentOrder?.db_order?.pdf_documents || [];
    const reports = [];

    pdfDocuments.forEach((doc) => {
      if (doc.mailman_order_id) {
        reports.push({ type: 'mailman', label: 'Semen Analysis Results' });
      }
      if (doc.title?.toLowerCase().includes('tasso')) {
        reports.push({ type: 'tasso', label: 'Hormone Panel Results' });
      }
    });

    return reports;
  };

  const handleGetSupplement = () => {
    window.open('https://thisisneeded.com/collections/mens-fertility/sperm-quality', '_blank');
  };

  // Helper function to calculate graph points
  const calculateGraphPoints = (testData) => {
    if (!testData || testData.length < 2) {
      return [];
    }

    const width = 301;
    const paddingX = 4;
    const paddingY = 10;
    const graphWidth = width - paddingX * 2;
    const graphHeight = 119 - paddingY * 2;

    const scores = testData.map((t) => t.score);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    const scoreRange = maxScore - minScore || 100;
    const paddingRatio = 0.1; // 10% padding at top and bottom

    const points = testData.map((test, index) => {
      const x = paddingX + (index / (testData.length - 1)) * graphWidth;
      const normalizedScore = (test.score - minScore) / scoreRange;
      const paddedNormalized = paddingRatio + normalizedScore * (1 - 2 * paddingRatio);
      const y = paddingY + graphHeight - paddedNormalized * graphHeight;
      return { x, y };
    });

    return points;
  };

  // Generate graph area path (for fill) - closed path from points to bottom
  const generateGraphAreaPath = (testData) => {
    if (!testData || testData.length < 2) {
      return 'M 4 119 L 4 119 L 297 119 Z';
    }

    const points = calculateGraphPoints(testData);
    const height = 119;

    // Create path connecting all points
    let path = `M ${points[0].x} ${points[0].y}`;

    for (let i = 1; i < points.length; i++) {
      if (i === 1) {
        // First segment: simple line
        path += ` L ${points[i].x} ${points[i].y}`;
      } else {
        // Subsequent segments: use smooth curves
        const prev = points[i - 2];
        const current = points[i - 1];
        const next = points[i];

        // Calculate control point for smooth curve
        const cpX = current.x + (next.x - prev.x) * 0.2;
        const cpY = current.y + (next.y - prev.y) * 0.2;

        path += ` Q ${cpX} ${cpY}, ${next.x} ${next.y}`;
      }
    }

    // Close path: line to bottom-right, bottom-left, then back to start
    path += ` L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;

    return path;
  };

  // Generate stroke path (line only, no fill)
  const generateGraphStrokePath = (testData) => {
    if (!testData || testData.length < 2) {
      return '';
    }

    const points = calculateGraphPoints(testData);

    // Create path connecting all points
    let path = `M ${points[0].x} ${points[0].y}`;

    for (let i = 1; i < points.length; i++) {
      if (i === 1) {
        // First segment: simple line
        path += ` L ${points[i].x} ${points[i].y}`;
      } else {
        // Subsequent segments: use smooth curves
        const prev = points[i - 2];
        const current = points[i - 1];
        const next = points[i];

        // Calculate control point for smooth curve
        const cpX = current.x + (next.x - prev.x) * 0.2;
        const cpY = current.y + (next.y - prev.y) * 0.2;

        path += ` Q ${cpX} ${cpY}, ${next.x} ${next.y}`;
      }
    }

    return path;
  };

  return (
    <div className="dashboard-page">
      {reportType === 'tasso' && (
        <section className="pt-5 pb-3">
          <div className="container">
            <h2 className="section-title mb-0">Hormone Panel Results</h2>
          </div>
        </section>
      )}
      {reportType === 'mailman' && (
        <>
          <section className="overall-scrore-section pt-0">
            <div className="container">
              {/* Main Grid Layout */}
              <div className="main-grid">
                {/* Left Column - Greeting & Tips */}
                <div className="left-column">
                  {/* Greeting Card */}
                  <div className="greeting-card">
                    {!propUserId && !isAdminView && <h3 className="greeting-title">Hey, {username}</h3>}
                    {currentOrder?.db_order?.pdf_documents?.length > 0 ? (
                      <>
                        <p className="greeting-text">
                          The latest results look good, you likely have strong fertility potential at the moment. Your sperm parameters indicate
                          healthy reproductive function.
                        </p>
                        {/* <a href="#" className="view-link" data-bs-toggle="modal" data-bs-target="#viewanalysisModal">
                          View the full analysis
                        </a> */}
                        {/* modal here */}
                        <div
                          className="modal fade viewanalysismodal"
                          id="viewanalysisModal"
                          tabIndex="-1"
                          aria-labelledby="viewanalysisModalLabel"
                          aria-hidden="true"
                        >
                          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content">
                              <div className="modal-header" style={{ position: 'relative', overflow: 'hidden' }}>
                                <video
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    zIndex: 0,
                                  }}
                                >
                                  <source src={mesh} type="video/mp4" />
                                </video>
                                <button
                                  type="button"
                                  className="btn-close"
                                  data-bs-dismiss="modal"
                                  aria-label="Close"
                                  style={{ position: 'relative', zIndex: 1 }}
                                ></button>
                              </div>
                              <div className="modal-body">
                                <h3 className="modal-title" id="viewanalysisModalLabel">
                                  Your latest results show clear improvement
                                </h3>
                                <p>
                                  Your recent test shows measurable progress compared to your previous result. Motility, concentration and morphology
                                  increased, which points to stronger fertility potential at the moment. This improvement suggests that some of the
                                  habits in your daily routine are having a positive impact on your reproductive health.
                                </p>
                                <p>
                                  The change in your numbers highlights why long-term tracking matters. Sperm health is influenced by sleep, exercise,
                                  stress, recovery and diet. Seeing progress from one result to the next gives confidence that consistent habits can
                                  drive biological change. Testing every three months helps you spot trends instead of guessing based on a single
                                  test.
                                </p>
                                <p>
                                  You can explore each metric in the dashboard to see which areas improved most, and if you use a wearable, you can
                                  compare your sleep and activity patterns with your sperm metrics. Small adjustments over time tend to accumulate.
                                  The next test will give even more clarity on which routines support your strongest performance.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="greeting-text">Welcome aboard! We are excited to help you track and improve your reproductive health.</p>
                    )}
                  </div>

                  {/* Tips Card */}
                  <div className="tips-card dash-card d-none d-md-block">
                    <h3 className="dash-card-title">Tips</h3>
                    <div
                      className="tips-carousel"
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={() => handleTouchEnd('tips')}
                    >
                      <div className="tips-track" style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
                        {tips.map((tip, index) => (
                          <div key={index} className="tip-slide">
                            <p className="tip-title">{tip.title}</p>
                            <p className="tip-description">{tip.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="carousel-controls">
                      <a href="#" className="view-link" data-bs-toggle="modal" data-bs-target="#tipsModal">
                        See more tips
                      </a>
                      {/* modal here */}
                      <div className="modal fade tipsmodal" id="tipsModal" tabIndex="-1" aria-labelledby="tipsModalLabel" aria-hidden="true">
                        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                          <div className="modal-content">
                            <div className="modal-header">
                              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                              <div className="graph"></div>
                            </div>
                            <div className="modal-body">
                              <div className="">
                                <span className="tag">LIFESTYLE</span>
                                <h3 className="modal-title" id="tipsModalLabel">
                                  How Sleep Impacts Your Health Every Day
                                </h3>
                              </div>
                              <p>
                                Sleep is one of the strongest factors that influence semen quality. Your body repairs, regulates hormones and builds
                                new cells during the night. When sleep is irregular, these processes become less stable, which can affect your
                                fertility baseline over the full two to three month cycle.
                              </p>
                              <p>Below are simple habits that help you get more solid rest.</p>
                              <div>
                                <p className="blue-text">1. Keep the same sleep and wake time</p>
                                <p>
                                  Your body likes rhythm. Going to bed and waking up at roughly the same time helps regulate testosterone and overall
                                  hormone balance. Try to keep your schedule steady, even on weekends.
                                </p>
                              </div>
                              <div>
                                <p className="blue-text">2. Limit screens before bed</p>
                                <p>
                                  Blue light makes it harder for your brain to slow down. Choose low light, put your phone away at least 30 to 45
                                  minutes before sleep and switch to calmer activities like reading or stretching.
                                </p>
                              </div>
                              <div>
                                <p className="blue-text">3. Create a darker and cooler room</p>
                                <p>
                                  A cool, dark space helps your body fall asleep faster. Use blackout curtains if possible, remove bright LEDs in the
                                  bedroom and keep the temperature slightly lower than during the day.
                                </p>
                              </div>
                              <div>
                                <p className="blue-text">4. Cut caffeine later in the day</p>
                                <p>
                                  Caffeine can stay active for hours. If you often struggle to fall asleep, try avoiding coffee and energy drinks
                                  after mid-afternoon. This small shift can make a big difference in your sleep rhythm.
                                </p>
                              </div>
                              <div>
                                <p className="blue-text">5. Build a wind-down routine</p>
                                <p>
                                  Your brain needs time to slow down. A short routine like a warm shower, breathing exercises or gentle stretches
                                  tells your body it’s time to switch off. Keep it simple and repeat it daily so it becomes natural.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Arrow + dots controls desktop */}
                      <div className="tips-controls d-none d-md-flex">
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => activeSlide > 0 && setActiveSlide(activeSlide - 1)}
                          disabled={activeSlide === 0}
                        >
                          ‹
                        </button>

                        {/* <div className="dots">
                      {Array.from({ length: totalActionSlides }).map((_, index) => (
                        <span key={index} className={index === activeMetricSlide ? 'active' : ''} onClick={() => setActiveMetricSlide(index)} />
                      ))}
                    </div> */}

                        <div className="carousel-dots">
                          {tips.map((_, index) => (
                            <button
                              key={index}
                              className={`dot ${index === activeSlide ? 'active' : ''}`}
                              onClick={() => setActiveSlide(index)}
                              aria-label={`Go to tip ${index + 1}`}
                            />
                          ))}
                        </div>

                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => activeSlide < totalActionSlides - 1 && setActiveSlide(activeSlide + 1)}
                          disabled={activeSlide === totalActionSlides - 1}
                        >
                          ›
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Center Column - Score */}
                <div className="center-column">
                  <div className="score-card dash-card">
                    <div className="score-header">
                      <h3 className="dash-card-title mb-0">Overall SwimScore</h3>
                      <div className="info-button-wrapper">
                        <button className="info-button" aria-label="Information" onClick={() => setShowSwimScoreModal(true)}>
                          <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M7.65406 5.31701C7.48073 5.31701 7.33406 5.25701 7.21406 5.13701C7.09406 5.01701 7.03406 4.87034 7.03406 4.69701C7.03406 4.52368 7.09406 4.37701 7.21406 4.25701C7.33406 4.13701 7.48073 4.07701 7.65406 4.07701C7.82073 4.07701 7.96073 4.13701 8.07406 4.25701C8.19406 4.37701 8.25406 4.52368 8.25406 4.69701C8.25406 4.87034 8.19406 5.01701 8.07406 5.13701C7.96073 5.25701 7.82073 5.31701 7.65406 5.31701ZM8.09406 6.20701V11.687H7.18406V6.20701H8.09406Z"
                              fill="white"
                            />
                            <path
                              d="M7.48145 0.357422C11.4288 0.357422 14.6064 3.4386 14.6064 7.21191C14.6062 10.9851 11.4286 14.0664 7.48145 14.0664C3.53441 14.0662 0.357633 10.985 0.357422 7.21191C0.357422 3.4387 3.53428 0.357581 7.48145 0.357422Z"
                              stroke="white"
                              strokeWidth="0.714286"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p className="score-description">
                      SwimScore analyzes the key semen metrics to provide an educational fertility score. The median score for men is 66 and the
                      minimum according to WHO metrics is 10.
                    </p>
                    <p className="total-subtitle">Male median is 66, WHO minimum is 10</p>
                    {/* <div className="score-tooltip">
                Your combined sperm metrics in one score. It shows how your results compare to World Health Organization reference levels for healthy
                fertility.
              </div> */}

                    <div className="score-visual">
                      <svg className="score-ring" viewBox="0 0 200 200">
                        <defs>
                          <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="100%" stopColor="#8b5cf6" />
                          </linearGradient>
                        </defs>
                        <circle className="score-ring-bg" cx="100" cy="100" r="85" fill="none" strokeWidth="10" />
                        <circle
                          className="score-ring-progress"
                          cx="100"
                          cy="100"
                          r="85"
                          fill="none"
                          strokeWidth="10"
                          strokeDasharray="534"
                          strokeDashoffset={overall_score != null ? 534 * (1 - overall_score / 100) : 534}
                          transform="rotate(-90 100 100)"
                        />
                      </svg>
                      <div className="score-value">
                        <span className="score-label">Your score</span>
                        <span className="score-number">{overall_score != null ? overall_score : '--'}</span>
                        {overall_score != null && (
                          <span className="score-icon">
                            <svg width="22" height="12" viewBox="0 0 22 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M20.4229 0.813477L12.0889 9.1475L7.18655 4.24513L0.813477 10.6182"
                                stroke="#569DFF"
                                strokeWidth="1.62698"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M14.54 0.813477H20.4229V6.69632"
                                stroke="#569DFF"
                                strokeWidth="1.62698"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="score-schedule">Schedule next test in 72 days</p>
                  </div>
                </div>

                {/* SwimScore Info Modal */}
                {showSwimScoreModal && (
                  <div className="swimscore-modal-overlay" onClick={() => setShowSwimScoreModal(false)}>
                    <div className="swimscore-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="swimscore-modal-header">
                        <h3>What is SwimScore™ ?</h3>
                        <button className="swimscore-modal-close" onClick={() => setShowSwimScoreModal(false)}>
                          ✕
                        </button>
                      </div>
                      <div className="swimscore-modal-body">
                        <p>
                          SwimScore™ summarises your semen analysis into a single number from 0 to 95. It is designed to make your results easier to
                          understand, track over time, and act on — without replacing the detail of your full lab report.
                        </p>

                        <h4>What is tested?</h4>
                        <p>
                          Your score is calculated from the four parameters with the strongest evidence base for male fertility outcomes. Each is
                          weighted by its relative contribution.
                        </p>
                        <ul>
                          <li>
                            <strong>Effective swimmers (TMSC)</strong> — Total count of motile sperm per ejaculate. The single most predictive measure
                            of natural conception potential. Weighted most heavily in your score.
                          </li>
                          <li>
                            <strong>Forward movement</strong> — The percentage of sperm moving efficiently in a forward direction. Reflects the
                            quality of movement, not just quantity.
                          </li>
                          <li>
                            <strong>Sperm shape</strong> — The percentage of sperm with normal morphology under strict criteria. Even small
                            improvements here have meaningful downstream impact.
                          </li>
                          <li>
                            <strong>DNA integrity</strong> — The percentage of sperm with intact DNA. Only included in your score if a fragmentation
                            test was completed.
                          </li>
                        </ul>

                        <h4>How is it calibrated?</h4>
                        <p>SwimScore is anchored to two fixed reference points drawn from published population data:</p>
                        <ul>
                          <li>
                            <strong>Score ~10</strong> — the WHO lower reference limit: the 5th percentile of men who achieved pregnancy within 12
                            months.
                          </li>
                          <li>
                            <strong>Score ~66</strong> — the median of all adult men in unscreened population data.
                          </li>
                        </ul>

                        <h4>Score bands</h4>
                        <ul>
                          <li>
                            <strong>Excellent (90–95):</strong> Top-range semen quality. Count, motility, and morphology are all performing at a high
                            level. Male factor is unlikely to play a meaningful role in conception outcomes. Scores above 90 represent a plateau —
                            further improvement shows diminishing biological returns, and the score is capped at 95 to reflect that no result implies
                            perfect fertility.
                          </li>
                          <li>
                            <strong>Very Good (80–90):</strong> Well above the population median. Parameters are performing well across the board.
                            Semen quality is unlikely to be a primary barrier to conception. Minor optimisations are possible but not necessary for
                            most men in this range.
                          </li>
                          <li>
                            <strong>Good (66–80):</strong> At or above the typical male median. Your score is at or above the median for all adult men
                            tested. Semen quality is unlikely to be the primary factor affecting conception. If a specific parameter is slightly below
                            reference, targeted supplementation over one spermatogenesis cycle (90 days) can typically address it.
                          </li>
                          <li>
                            <strong>Below Typical (35–66):</strong> Below the population median. Your score falls below the median for all adult men.
                            This is a wide band — a score of 60 and a score of 35 have meaningfully different implications, and your individual
                            parameter breakdown will tell you more than the composite alone. For scores in the upper half of this range (50 to 66):
                            parameters are below average but not severely impaired. Lifestyle changes and targeted supplementation typically produce
                            measurable improvement within 90 days. For scores in the lower half (35 to 50): impairment is more significant.
                            Intervention is likely to help, but the driver matters. Reviewing your parameter breakdown and considering a hormonal
                            assessment is a reasonable next step.
                          </li>
                          <li>
                            <strong>Low (10–35):</strong> Approaching the WHO lower reference point. Your score is below typical population values and
                            approaching the WHO lower reference marker. Parameters at this level indicate meaningful impairment in at least one key
                            area. Targeted intervention — lifestyle, supplementation, and where appropriate a hormonal assessment with a physician —
                            will make a measurable difference. A 90-day improvement window applies here as it does in higher bands, but professional
                            guidance is likely to accelerate your path forward.
                          </li>
                          <li>
                            <strong>Below WHO Reference (0–10):</strong> At or below the WHO lower reference limit. Your score is at or below the 5th
                            percentile of men who achieved pregnancy within 12 months. At this level, lifestyle changes alone are unlikely to fully
                            address the underlying impairment — there are typically biological drivers that warrant investigation. This score does not
                            indicate permanent infertility. It indicates that professional evaluation is the clearest next step. A physician review
                            can identify whether hormonal, structural, or other treatable factors are contributing.
                          </li>
                        </ul>

                        <p className="swimscore-modal-disclaimer">
                          SwimScore is an educational and monitoring tool. It does not diagnose infertility, predict pregnancy with certainty, or
                          replace clinical judgment.Female partner factors are critical to conception outcomes and are not reflected in this score.
                          Your full lab report is available for physician review.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Right Column - Progress & Actions */}
                <div className="right-column">
                  {/* Overtime Progress Card */}
                  <div className="progress-card dash-card">
                    <div className="progress-header">
                      <h3 className="dash-card-title mb-0">Overtime Progress</h3>
                      <button className="info-button" aria-label="Information">
                        <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M7.65406 5.31701C7.48073 5.31701 7.33406 5.25701 7.21406 5.13701C7.09406 5.01701 7.03406 4.87034 7.03406 4.69701C7.03406 4.52368 7.09406 4.37701 7.21406 4.25701C7.33406 4.13701 7.48073 4.07701 7.65406 4.07701C7.82073 4.07701 7.96073 4.13701 8.07406 4.25701C8.19406 4.37701 8.25406 4.52368 8.25406 4.69701C8.25406 4.87034 8.19406 5.01701 8.07406 5.13701C7.96073 5.25701 7.82073 5.31701 7.65406 5.31701ZM8.09406 6.20701V11.687H7.18406V6.20701H8.09406Z"
                            fill="white"
                          />
                          <path
                            d="M7.48145 0.357422C11.4288 0.357422 14.6064 3.4386 14.6064 7.21191C14.6062 10.9851 11.4286 14.0664 7.48145 14.0664C3.53441 14.0662 0.357633 10.985 0.357422 7.21191C0.357422 3.4387 3.53428 0.357581 7.48145 0.357422Z"
                            stroke="white"
                            strokeWidth="0.714286"
                          />
                        </svg>
                      </button>
                    </div>
                    {/* Conditionally render based on number of tests */}
                    {overtimeTestData.length >= 2 && (
                      /* actual data graph - show when 2+ tests */
                      <div className="data-graph">
                        <svg width="100%" height="119" viewBox="0 0 301 119" fill="none" xmlns="http://www.w3.org/2000/svg">
                          {/* Graph area fill */}
                          <path d={generateGraphAreaPath(overtimeTestData)} fill="#000D31" />
                          <path d={generateGraphAreaPath(overtimeTestData)} fill="url(#pattern_overtime)" fillOpacity="0.04" />
                          <path d={generateGraphAreaPath(overtimeTestData)} fill="url(#paint_linear_overtime)" fillOpacity="0.57" />

                          {/* Graph stroke line */}
                          <path d={generateGraphStrokePath(overtimeTestData)} stroke="#0202F8" strokeWidth="1" fill="none" />

                          {/* Data points (circles) with hover */}
                          {(() => {
                            const width = 301;
                            const height = 119;
                            const paddingX = 4;
                            const paddingY = 10;
                            const graphWidth = width - paddingX * 2;
                            const graphHeight = height - paddingY * 2;
                            const scores = overtimeTestData.map((t) => t.score);
                            const minScore = Math.min(...scores);
                            const maxScore = Math.max(...scores);
                            const scoreRange = maxScore - minScore || 100;
                            const paddingRatio = 0.1;

                            return overtimeTestData.map((test, index) => {
                              const x = paddingX + (index / (overtimeTestData.length - 1)) * graphWidth;
                              const normalizedScore = (test.score - minScore) / scoreRange;
                              const paddedNormalized = paddingRatio + normalizedScore * (1 - 2 * paddingRatio);
                              const y = paddingY + graphHeight - paddedNormalized * graphHeight;
                              return <circle key={`point-${index}`} cx={x} cy={y} r="2.5" fill="#020899" stroke="#EEF3FC" strokeWidth="1" />;
                            });
                          })()}

                          <defs>
                            <linearGradient id="paint_linear_overtime" x1="150.5" y1="0" x2="150.5" y2="119" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#0202F8" />
                              <stop offset="1" stopColor="#0202F8" stopOpacity="0" />
                            </linearGradient>

                            <pattern
                              id="pattern_overtime"
                              patternUnits="userSpaceOnUse"
                              patternTransform="matrix(1500 0 0 1500 -598.5 -689.202)"
                              preserveAspectRatio="none"
                              viewBox="0 0 2000 2000"
                              width="1"
                              height="1"
                            >
                              <rect y="1280" width="1810.19" height="1018.23" transform="rotate(-45 0 1280)" fill="url(#pattern1_overtime)" />
                            </pattern>

                            <pattern id="pattern1_overtime" patternContentUnits="objectBoundingBox" width="1" height="1">
                              <use xlinkHref="#image_overtime" transform="scale(0.000520833 0.000925926)" />
                            </pattern>

                            <image
                              id="image_overtime"
                              width="1920"
                              height="1080"
                              preserveAspectRatio="none"
                              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAB4AAAAQ4CAYAAADo08FDAAA..."
                            />
                          </defs>
                        </svg>
                      </div>
                    )}
                    {overtimeTestData.length < 2 && (
                      /* for empty data - show when 0 or 1 test */
                      <>
                        <div className="progress-content">
                          <div className="progress-placeholder">
                            <svg
                              width="auto"
                              height="135"
                              viewBox="0 0 409 135"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlnsXlink="http://www.w3.org/1999/xlink"
                            >
                              <g opacity="0.2">
                                <path
                                  d="M0.149414 54.521L52.8573 71.1231C55.587 71.9829 58.568 71.3191 60.6749 69.3823L131.169 4.57824C134.044 1.93522 138.408 1.75434 141.491 4.15034L202.754 51.7466C204.97 53.4682 207.926 53.9029 210.544 52.8921L271.116 29.505C272.156 29.1035 273.269 28.9237 274.382 28.9772L350.418 32.6309C351.538 32.6847 352.658 32.5023 353.703 32.0953L408.149 10.9042V134.122H0.149414V54.521Z"
                                  fill="#000D31"
                                />

                                <path
                                  d="M0.149414 54.521L52.8573 71.1231C55.587 71.9829 58.568 71.3191 60.6749 69.3823L131.169 4.57824C134.044 1.93522 138.408 1.75434 141.491 4.15034L202.754 51.7466C204.97 53.4682 207.926 53.9029 210.544 52.8921L271.116 29.505C272.156 29.1035 273.269 28.9237 274.382 28.9772L350.418 32.6309C351.538 32.6847 352.658 32.5023 353.703 32.0953L408.149 10.9042V134.122H0.149414V54.521Z"
                                  fill="url(#pattern0_179_1030)"
                                  fillOpacity="0.04"
                                />

                                <path
                                  d="M0.149414 54.521L52.8573 71.1231C55.587 71.9829 58.568 71.3191 60.6749 69.3823L131.169 4.57824C134.044 1.93522 138.408 1.75434 141.491 4.15034L202.754 51.7466C204.97 53.4682 207.926 53.9029 210.544 52.8921L271.116 29.505C272.156 29.1035 273.269 28.9237 274.382 28.9772L350.418 32.6309C351.538 32.6847 352.658 32.5023 353.703 32.0953L408.149 10.9042V134.122H0.149414V54.521Z"
                                  fill="url(#paint0_linear_179_1030)"
                                  fillOpacity="0.57"
                                />

                                <path
                                  d="M0.149414 54.235L52.8762 70.7561C55.5959 71.6082 58.5636 70.9491 60.6669 69.0257L131.183 4.54131C134.054 1.91637 138.396 1.73652 141.474 4.11511L202.765 51.4836C204.976 53.1921 207.918 53.6235 210.526 52.6218L271.123 29.3476C272.159 28.9499 273.266 28.7719 274.374 28.8249L350.426 32.4602C351.541 32.5135 352.655 32.3328 353.696 31.9297L408.149 10.847"
                                  stroke="#0202F8"
                                />

                                <circle cx="137.088" cy="2.81768" r="2.31768" stroke="white" />
                                <circle cx="57.0667" cy="71.5691" r="2.31768" stroke="white" />
                                <circle cx="352.923" cy="31.5581" r="1.75414" stroke="white" />
                                <circle cx="272.9" cy="29.3039" r="1.75414" stroke="white" />
                                <circle cx="206.404" cy="52.9724" r="1.75414" stroke="white" />
                              </g>

                              <defs>
                                <linearGradient
                                  id="paint0_linear_179_1030"
                                  x1="204.149"
                                  y1="0"
                                  x2="204.149"
                                  y2="134.122"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#0202F8" />
                                  <stop offset="1" stopColor="#0202F8" stopOpacity="0" />
                                </linearGradient>

                                <pattern
                                  id="pattern0_179_1030"
                                  patternUnits="userSpaceOnUse"
                                  patternTransform="matrix(1500 0 0 1500 -545.851 -682.062)"
                                  preserveAspectRatio="none"
                                  viewBox="0 0 2000 2000"
                                  width="1"
                                  height="1"
                                >
                                  <rect y="1280" width="1810.19" height="1018.23" transform="rotate(-45 0 1280)" fill="url(#pattern1_179_1030)" />
                                </pattern>

                                <pattern id="pattern1_179_1030" patternContentUnits="objectBoundingBox" width="1" height="1">
                                  <use xlinkHref="#image0_179_1030" transform="scale(0.000520833 0.000925926)" />
                                </pattern>

                                <image
                                  id="image0_179_1030"
                                  width="1920"
                                  height="1080"
                                  preserveAspectRatio="none"
                                  xlinkHref="data:image/png;base64,...."
                                />
                              </defs>
                            </svg>
                          </div>
                        </div>
                        <div className="progress-cta">
                          <p className="start-button">Start testing!</p>
                          <p className="progress-note">More data required for insights</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Tips Card for mobile view*/}
                  <div className="tips-card dash-card d-block d-md-none">
                    <h3 className="dash-card-title">Tips</h3>
                    <div
                      className="tips-carousel"
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={() => handleTouchEnd('tips')}
                    >
                      <div className="tips-track" style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
                        {tips.map((tip, index) => (
                          <div key={index} className="tip-slide">
                            <p className="tip-title">{tip.title}</p>
                            <p className="tip-description">{tip.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="carousel-controls">
                      <a href="#" className="view-link">
                        See more tips
                      </a>
                      <div className="carousel-dots">
                        {tips.map((_, index) => (
                          <button
                            key={index}
                            className={`dot ${index === activeSlide ? 'active' : ''}`}
                            onClick={() => setActiveSlide(index)}
                            aria-label={`Go to tip ${index + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions Card */}
                  <div className="quick-actions-card">
                    <div className="quick-actions-header">
                      <h3 className="quick-actions-title dash-card-title">Quick action</h3>

                      {/* Arrow + dots controls desktop */}
                      <div className="quick-actions-controls d-none d-md-flex">
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => activeMetricSlide > 0 && setActiveMetricSlide(activeMetricSlide - 1)}
                          disabled={activeMetricSlide === 0}
                        >
                          ‹
                        </button>

                        <div className="dots">
                          {Array.from({ length: totalActionSlides }).map((_, index) => (
                            <span key={index} className={index === activeMetricSlide ? 'active' : ''} onClick={() => setActiveMetricSlide(index)} />
                          ))}
                        </div>

                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => activeMetricSlide < totalActionSlides - 1 && setActiveMetricSlide(activeMetricSlide + 1)}
                          disabled={activeMetricSlide === totalActionSlides - 1}
                        >
                          ›
                        </button>
                      </div>
                    </div>

                    <div className="actions-carousel">
                      <div
                        className="actions-track"
                        style={{
                          transform: `translateX(-${activeMetricSlide * 100}%)`,
                        }}
                      >
                        {Array.from({ length: totalActionSlides }).map((_, slideIndex) => (
                          <div key={slideIndex} className="actions-slide">
                            <div className="quick-actions-grid">
                              {quickActions.slice(slideIndex * 2, slideIndex * 2 + 2).map((action, index) => (
                                <div key={index} className="action-item dash-card">
                                  <h4 className="action-title">{action.title}</h4>
                                  {action.title === 'Order new supplements' ? (
                                    <button
                                      className="action-btn"
                                      onClick={() => window.open('https://thisisneeded.com/collections/mens-fertility/sperm-quality', '_blank')}
                                    >
                                      {action.buttonText}
                                    </button>
                                  ) : action.title === 'Expert Consultation' ? (
                                    <button
                                      className="action-btn"
                                      onClick={() => window.open('https://calendly.com/myswimscore-info/30min', '_blank')}
                                    >
                                      {action.buttonText}
                                    </button>
                                  ) : action.title === 'Export your data' ? (
                                    <button
                                      className="action-btn"
                                      onClick={() => document.querySelector('.followup-section')?.scrollIntoView({ behavior: 'smooth' })}
                                    >
                                      {action.buttonText}
                                    </button>
                                  ) : (
                                    <button className="action-btn">{action.buttonText}</button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Arrow + dots controls mobile */}
                    <div className="quick-actions-controls mt-3 d-flex d-md-none">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => activeMetricSlide > 0 && setActiveMetricSlide(activeMetricSlide - 1)}
                        disabled={activeMetricSlide === 0}
                      >
                        ‹
                      </button>

                      <div className="dots">
                        {Array.from({ length: totalActionSlides }).map((_, index) => (
                          <span key={index} className={index === activeMetricSlide ? 'active' : ''} onClick={() => setActiveMetricSlide(index)} />
                        ))}
                      </div>

                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => activeMetricSlide < totalActionSlides - 1 && setActiveMetricSlide(activeMetricSlide + 1)}
                        disabled={activeMetricSlide === totalActionSlides - 1}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          {false && (
            <section className="keymetrics-section">
              <div className="container">
                <div className="row">
                  <div className="col-md-8">
                    {/* Key Metrics */}
                    <div className="metrics-block">
                      <h3 className="section-title">Key Metrics</h3>
                      {currentTest && currentTest.keyMetrics && currentTest.keyMetrics.length > 0 ? (
                        <KeyMetrics
                          keyMetrics={currentTest.keyMetrics}
                          isFirstTest={
                            !currentTest.previousTest || !currentTest.previousTest.keyMetrics || currentTest.previousTest.keyMetrics.length === 0
                          }
                        />
                      ) : (
                        <span className="no-data" role="status">
                          No metrics data available
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    {/* Recent Progress */}
                    <div className="progress-block">
                      <h3 className="section-title">Recent Progress</h3>
                      {userData && userData.testHistory && userData.testHistory.length > 0 ? (
                        <>
                          {/* Test History Cards - Clickable
                    <div className="test-history-cards" style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                      {userData.testHistory.map((test, index) => {
                        // Match by date and overallScore (swimscore_overall_score)
                        const isSelected =
                          selectedTest && test.date === selectedTest.date && Number(test.overallScore) === Number(selectedTest.overallScore);
                        // Use test.date for display (actual test date) or fallback to order date
                        const displayDate = test.testData?.date || test.date;
                        return (
                          <div
                            key={index}
                            onClick={() => handleTestSelect({ date: test.date, overallScore: test.overallScore })}
                            style={{
                              padding: '12px 16px',
                              border: isSelected ? '2px solid #0202F8' : '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? 'rgba(2, 2, 248, 0.1)' : 'transparent',
                              transition: 'all 0.2s',
                            }}
                          >
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
                              {displayDate ? format(new Date(displayDate), 'MMM d, yyyy') : 'Date N/A'}
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff' }}>
                              Score: {test.overallScore !== null && test.overallScore !== undefined ? test.overallScore : '--'}
                            </div>
                          </div>
                        );
                      })}
                      {selectedTest && (
                        <button
                          onClick={() => setSelectedTest(null)}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            color: '#fff',
                            fontSize: '12px',
                          }}
                        >
                          Show All
                        </button>
                      )}
                    </div> */}
                          <Progress userData={userData} selectedTest={selectedTest} />
                        </>
                      ) : (
                        <span className="no-data" role="status">
                          No progress data available
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}
      <section className="detailed-analysis-section">
        <div className="container">
          <h3 className="section-title">Detailed Analysis</h3>

          {/* Show tabs only if both types exist in db_order */}
          {availableAnalysisTabs.length > 1 && (
            <div className="analysis-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button
                onClick={() => setActiveAnalysisTab('mailman')}
                style={{
                  padding: '10px 20px',
                  border: activeAnalysisTab === 'mailman' ? '2px solid #0202F8' : '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  backgroundColor: activeAnalysisTab === 'mailman' ? 'rgba(2, 2, 248, 0.1)' : 'transparent',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: activeAnalysisTab === 'mailman' ? 'bold' : 'normal',
                  transition: 'all 0.2s',
                }}
              >
                Sperm Analysis
              </button>
              <button
                onClick={() => setActiveAnalysisTab('tasso')}
                style={{
                  padding: '10px 20px',
                  border: activeAnalysisTab === 'tasso' ? '2px solid #0202F8' : '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  backgroundColor: activeAnalysisTab === 'tasso' ? 'rgba(2, 2, 248, 0.1)' : 'transparent',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: activeAnalysisTab === 'tasso' ? 'bold' : 'normal',
                  transition: 'all 0.2s',
                }}
              >
                Hormone Analysis
              </button>
            </div>
          )}

          {(mailmanCategories.length > 0 || tassoCategories.length > 0) && (
            <p className="section-subtitle">Expand each metric to see detailed information and personalized recommendations</p>
          )}

          {/* Display Mailman (Sperm Analysis) data */}
          {activeAnalysisTab === 'mailman' && mailmanCategories.length > 0 && (
            <>
              {mailmanCategories.map((category) => {
                const categoryMetrics = mailmanMetrics.filter((metric) => metric.categoryName === category.name);
                const isOpen = accordionStates[`mailman-${category.name}`] || false;

                return (
                  <div key={category.name} className="detailed-analysis-container dash-card">
                    <div
                      className="acc-header"
                      onClick={() => {
                        setAccordionStates((prev) => ({
                          ...prev,
                          [`mailman-${category.name}`]: !prev[`mailman-${category.name}`],
                        }));
                      }}
                    >
                      <p className="acc-title">{category.name}</p>
                      <span className={`chevron ${!isOpen ? 'open' : ''}`}>
                        <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13 7L7 1L1 7" stroke="#FCFCFC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </div>
                    {isOpen && categoryMetrics.length > 0 && (
                      <div className="metrics-container">
                        {categoryMetrics.map((metric, index) => {
                          const whoData = findWhoCutoff(metric.name);
                          const metricWithWhoCutoff = {
                            ...metric,
                            who_cutoff: whoData?.who_cutoff || null,
                            range_low: whoData?.low || null,
                            range_normal: whoData?.normal || null,
                            range_great: whoData?.great || null,
                            range_unit: whoData?.range_unit || null,
                            label_low: whoData?.label_low || null,
                            label_normal: whoData?.label_normal || null,
                            label_great: whoData?.label_great || null,
                          };
                          return <MetricCard key={`mailman-metric-${category.name}-${metric.name}-${index}`} metric={metricWithWhoCutoff} />;
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* Display Tasso (Hormone Analysis) data */}
          {activeAnalysisTab === 'tasso' && tassoCategories.length > 0 && (
            <>
              {tassoCategories.map((category) => {
                const categoryMetrics = tassoMetrics.filter((metric) => metric.categoryName === category.name);
                const isOpen = accordionStates[`tasso-${category.name}`] || false;

                return (
                  <div key={category.name} className="detailed-analysis-container dash-card">
                    <div
                      className="acc-header"
                      onClick={() => {
                        setAccordionStates((prev) => ({
                          ...prev,
                          [`tasso-${category.name}`]: !prev[`tasso-${category.name}`],
                        }));
                      }}
                    >
                      <p className="acc-title">{category.name}</p>
                      <span className={`chevron ${!isOpen ? 'open' : ''}`}>
                        <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13 7L7 1L1 7" stroke="#FCFCFC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </div>
                    {isOpen && categoryMetrics.length > 0 && (
                      <div className="metrics-container">
                        {categoryMetrics.map((metric, index) => {
                          const whoData = findWhoCutoff(metric.name);
                          const metricWithWhoCutoff = {
                            ...metric,
                            who_cutoff: whoData?.who_cutoff || null,
                            range_low: whoData?.low || null,
                            range_normal: whoData?.normal || null,
                            range_great: whoData?.great || null,
                            range_unit: whoData?.range_unit || null,
                            label_low: whoData?.label_low || null,
                            label_normal: whoData?.label_normal || null,
                            label_great: whoData?.label_great || null,
                          };
                          return <MetricCard key={`tasso-metric-${category.name}-${metric.name}-${index}`} metric={metricWithWhoCutoff} />;
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* Show empty state if no data available */}
          {mailmanCategories.length === 0 && tassoCategories.length === 0 && (
            <span className="no-data" role="status">
              No Detailed Analysis data available
            </span>
          )}
        </div>
      </section>
      {reportType === 'mailman' && (
        <>
          <section className="followup-section">
            <div className="container">
              <h3 className="section-title">Follow-up steps</h3>
              <p className="section-subtitle">Things you can do to start improving your Swimscore.</p>

              {/* Slider */}
              <div className="followup-slider">
                <div
                  className="followup-track row"
                  style={{
                    transform: isMobile ? `translateX(-${followupActive * 319}px)` : 'none',
                  }}
                >
                  <div className="col-md-4">
                    <div className="followup-card" style={{ position: 'relative', overflow: 'hidden', background: 'none' }}>
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          zIndex: 0,
                        }}
                      >
                        <source src={consultVideo} type="video/mp4" />
                      </video>
                      <div className="upper-block" style={{ position: 'relative', zIndex: 1 }}>
                        <h4>Expert Consultation</h4>
                        <p>Speak with a fertility specialist to review your results, understand your metrics, and get a personalized plan.</p>
                      </div>
                      <div className="lower-block" style={{ position: 'relative', zIndex: 1 }}>
                        <button
                          className="btn btn-primary w-fit px-4"
                          onClick={() => window.open('https://calendly.com/myswimscore-info/30min', '_blank')}
                        >
                          Book a Consultation
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="followup-card card-two">
                      <div className="upper-block">
                        <h4>Get supplements</h4>
                        <p>Optimize your reproductive health with specialized male fertility supplements.</p>
                      </div>
                      <div className="lower-block">
                        <button className="btn btn-primary w-fit px-4" onClick={handleGetSupplement}>
                          Get Supplements
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="followup-card card-three">
                      <div className="upper-block">
                        <h4>Export your data</h4>
                        <p>Download your fertility results to track trends and compare with other health data.</p>
                      </div>
                      <div className="lower-block">
                        {(() => {
                          const availableReports = getAvailableReports();

                          if (availableReports.length === 1) {
                            return (
                              <button className="btn btn-primary w-fit px-4" onClick={() => handleDownloadData(availableReports[0].type)}>
                                Download Data
                              </button>
                            );
                          } else if (availableReports.length > 1) {
                            return (
                              <div className="dropdown" style={{ position: 'relative' }}>
                                <button
                                  className="btn btn-primary w-fit px-4 dropdown-toggle"
                                  onClick={(e) => {
                                    e.currentTarget.nextElementSibling.style.display =
                                      e.currentTarget.nextElementSibling.style.display === 'block' ? 'none' : 'block';
                                  }}
                                >
                                  Download Data ▼
                                </button>
                                <div
                                  className="dropdown-menu"
                                  style={{
                                    display: 'none',
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    backgroundColor: '#1a1a2e',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '8px',
                                    minWidth: '150px',
                                    zIndex: 1000,
                                  }}
                                >
                                  {availableReports.map((report) => (
                                    <button
                                      key={report.type}
                                      onClick={() => {
                                        handleDownloadData(report.type);
                                        document.querySelector('.dropdown-menu').style.display = 'none';
                                      }}
                                      style={{
                                        display: 'block',
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: 'none',
                                        backgroundColor: 'transparent',
                                        color: '#fff',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                      }}
                                      onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgba(255,255,255,0.1)')}
                                      onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                                    >
                                      {report.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          }

                          return (
                            <button className="btn btn-primary w-fit px-4" disabled>
                              No reports available
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls – MOBILE ONLY */}
              {isMobile && (
                <div className="followup-controls">
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => followupActive > 0 && setFollowupActive(followupActive - 1)}
                    disabled={followupActive === 0}
                  >
                    ‹
                  </button>

                  <div className="dots">
                    {[0, 1, 2].map((_, i) => (
                      <span key={i} className={i === followupActive ? 'active' : ''} />
                    ))}
                  </div>

                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => followupActive < 2 && setFollowupActive(followupActive + 1)}
                    disabled={followupActive === 2}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </section>
        </>
      )}
      {/* <Footer /> */}
    </div>
  );
};

Dashboard.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userName: PropTypes.string,
  isAdminView: PropTypes.bool,
};

export default Dashboard;
